import React, { useMemo } from 'react';
import { graphql, HeadProps, PageProps } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { TemplateContainer } from './ResourceCenter.styles';
import { Attachments, MarkupElement, MarkupValue } from 'src/functions/formatElement.htmlAst';
import calculateEstimatedReadingTime from 'src/functions/calculateEstimatedReadingTime';
import { hasEmbedInstagramPostRecursive } from 'src/functions/getEmbedInstagramPostHTML';
import ResourceBottomCallToAction from 'app_previous/organisms/Shared/Articles/ResourceBottomCallToAction/ResourceBottomCallToAction';
import { HeadComponent } from 'app_previous/elements/Layout/head';
import ResourceHeroBanner from 'app_previous/organisms/Shared/Articles/ResourceHeroBanner/ResourceHeroBanner';
import MoreLikeThisCarousel from 'app_previous/organisms/Shared/Articles/MoreLikeThisCarousel/MoreLikeThisCarousel';
import JoinMightyCommunityBanner from 'app_previous/organisms/Standard/ResourceCenterArticle/JoinMightyCommunityBanner/JoinMightyCommunityBanner';
import ResourceCenterMenu from 'app_previous/organisms/Standard/Shared/Articles/ResourceCenter/Menu';
import ArticleBodyWithTableOfContents from 'app_previous/organisms/Standard/Shared/ArticleBodyWithTableOfContents/ArticleBodyWithTableOfContents';
import GlobalMain from 'molecules/Main/GlobalMain';

interface ResourceCenterPageContext {
  hasTableOfContents: boolean;
}

interface ResourceCenterProps extends PageProps<unknown, ResourceCenterPageContext> {
  data: {
    allContentfulResource: {
      edges: {
        node: {
          title: string;
          slug: string;
          category: string;
          subCategory: string;
          heroImage: { gatsbyImageData: IGatsbyImageData };
        };
      }[];
    };
    contentfulResource: {
      id: string;
      title: string;
      slug: string;
      seoTitle?: string;
      seoDescription?: string;
      author?: string;
      contentful_id: string;
      category: string;
      updatedAt: string;
      dateText?: string;
      description: { description: string };
      canonicalTagSlug?: string;
      ctaBottomText?: string;
      heroImage?: {
        gatsbyImageData: IGatsbyImageData;
        file?: {
          url: string;
        };
      };
      attachments: Attachments;
      body: {
        internal: { type: string };
        childMarkdownRemark: {
          wordCount?: {
            words?: number;
          };
          htmlAst: {
            type: string;
            children: MarkupElement[];
          };
        };
      };
      bodyOverflow?: {
        internal: { type: string };
        childMarkdownRemark: {
          wordCount?: {
            words?: number;
          };
          htmlAst: {
            type: string;
            children: MarkupElement[];
          };
        };
      };
      customPopupModalTitle?: string;
      customPopupModalDescription?: {
        customPopupModalDescription?: string;
      };
      customPopupModalDismissId?: string;
    };
  };
}

const ResourceCenterTemplate = (props: ResourceCenterProps): JSX.Element => {
  const articleData = props.data.contentfulResource;
  const titleImage = articleData.heroImage?.gatsbyImageData || null;

  const bodyWords = articleData.body.childMarkdownRemark?.wordCount?.words || 0;
  const bodyOverflowWords = articleData.bodyOverflow?.childMarkdownRemark?.wordCount?.words || 0;

  const readTime = calculateEstimatedReadingTime(bodyWords + bodyOverflowWords);

  const dateTextDateObject = new Date(articleData.dateText);
  const dateText = !articleData.dateText
    ? articleData.updatedAt
    : `${dateTextDateObject.toLocaleString('default', {
        month: 'long',
        day: 'numeric',
      })}, ${dateTextDateObject.getFullYear()}`;

  const hasTableOfContents = props.pageContext.hasTableOfContents;

  // NOTE: Temporal Fix,
  // >> removes Contentful Red text CTA and leave new Body Footer CTA in place
  const filteredHtmlAst = useMemo(() => {
    let filteredArticleData = articleData.body.childMarkdownRemark.htmlAst.children;
    const bodyOverflow = articleData.bodyOverflow?.childMarkdownRemark?.htmlAst?.children || [];

    filteredArticleData = [...filteredArticleData, ...bodyOverflow];

    const redCtaElementIndex = filteredArticleData.findIndex(
      (mE: MarkupElement) => mE.properties?.style && mE.tagName === 'p'
    );

    // Temporary fix to remove the "in this article..." text
    // remove this once we have made a cleanup on the articles
    filteredArticleData.forEach((mE: MarkupElement) => {
      const recursiveDigIntoElement = (element: MarkupValue | MarkupElement, parent: MarkupElement): MarkupValue => {
        if (
          element.type === 'text' &&
          ['in this article…', 'in this article...'].includes(element.value.toLocaleLowerCase())
        ) {
          parent.children.splice(parent.children.indexOf(element), 1);
          return;
        }

        if (!(element as MarkupElement).children) return;

        (element as MarkupElement).children.forEach((childrenElement) =>
          recursiveDigIntoElement(childrenElement, element as MarkupElement)
        );
      };

      if (!mE.children) return;

      mE.children.forEach((childrenElement) => recursiveDigIntoElement(childrenElement, mE as MarkupElement));
    });

    if (redCtaElementIndex > 0) {
      filteredArticleData = filteredArticleData.filter((_, i) => i < redCtaElementIndex);
    }

    return filteredArticleData;
  }, [articleData]);

  const carouselArticles = useMemo(
    () =>
      props.data.allContentfulResource.edges
        .filter((e) => !!e.node.heroImage?.gatsbyImageData && e.node.slug !== articleData.slug)
        .map((edge) => ({
          title: edge.node.title,
          url: `/resources/${edge.node.slug}`,
          image: edge.node.heroImage?.gatsbyImageData,
          isInternal: true,
        })),
    [props.data.allContentfulResource.edges, articleData]
  );
  const popupModalCustomization =
    articleData.customPopupModalTitle || articleData.customPopupModalDescription?.customPopupModalDescription
      ? {
          title: articleData.customPopupModalTitle,
          description: articleData.customPopupModalDescription?.customPopupModalDescription,
          shownStorageId: articleData.customPopupModalDismissId || articleData.slug,
        }
      : undefined;

  return (
    <GlobalMain
      location={props.location}
      popupCustomization={popupModalCustomization}
    >
      <TemplateContainer>
        <ResourceHeroBanner
          titleImage={titleImage}
          category={articleData.category}
          title={articleData.title}
          description={articleData.description?.description}
          author={articleData.author ?? 'The Mighty Team'}
          updatedAt={dateText}
          readTime={readTime}
        />
        <ArticleBodyWithTableOfContents
          hasTableOfContents={hasTableOfContents}
          contentHtmlAst={filteredHtmlAst}
          articleData={articleData}
          contentId='resource-center-article-body'
          contentDataSectionName='Resource Center Article'
        />
        <ResourceBottomCallToAction ctaBottomText={articleData.ctaBottomText} />
        <MoreLikeThisCarousel posts={carouselArticles} />
        <JoinMightyCommunityBanner />
        <ResourceCenterMenu
          dropdownVerticalOrientation='top'
          backgroundColor={'darkAccentBlue'}
          hoverColor={'spruce'}
        />
      </TemplateContainer>
    </GlobalMain>
  );
};

export default ResourceCenterTemplate;

export const Head = (props: HeadProps<ResourceCenterProps['data']>): JSX.Element => {
  const articleData = props.data.contentfulResource;

  const hasInstagramData = useMemo(
    () => hasEmbedInstagramPostRecursive(articleData.body.childMarkdownRemark.htmlAst.children),
    [articleData]
  );

  const bodyWords = articleData.body.childMarkdownRemark?.wordCount?.words || 0;
  const bodyOverflowWords = articleData.bodyOverflow?.childMarkdownRemark?.wordCount?.words || 0;

  const readTime = calculateEstimatedReadingTime(bodyWords + bodyOverflowWords);

  const helmet = {
    title: articleData.seoTitle || articleData.title,
    description: articleData.seoDescription || articleData.description?.description,
    previewImageURL: props.data.contentfulResource.heroImage?.file.url,
    author: articleData.author ?? 'The Mighty Team',
    estimatedReadingTime: readTime,
  };

  return (
    <HeadComponent
      helmet={helmet}
      location={props.location}
      canonicalTagSlug={
        props.data.contentfulResource.canonicalTagSlug ? `/${props.data.contentfulResource.canonicalTagSlug}` : null
      }
      hasEmbedInstagramPost={hasInstagramData}
    />
  );
};

export const pageQuery = graphql`
  query ResourceCenterBySlug($slug: String!, $category: String!) {
    contentfulResource(slug: { eq: $slug }) {
      ...ResourceContent
    }
    allContentfulResource(limit: 20, filter: { category: { eq: $category }, slug: { ne: "attachments-testing" } }) {
      edges {
        node {
          id
          title
          slug
          contentful_id
          subCategory
          category
          description {
            description
          }
          heroImage {
            gatsbyImageData(width: 1200, layout: CONSTRAINED, placeholder: BLURRED)
          }
        }
      }
    }
  }
`;
